/**
 * @file navigation-key.ts
 * @description Resolves a pathname to the `navigationConfig` key whose sidebar
 * the page should render.
 *
 * The school dashboard is split into four sections that all live under
 * `/school/*` (`manage`, `comms`, `events`, `support`), so the bare first path
 * segment ("school") is NOT a usable key — it resolves to nothing and the page
 * renders an empty or wrong sidebar. Every `/school/*` route must therefore
 * match on its SECOND segment.
 *
 * Both the desktop sidebar (`app-sidebar.tsx`) and the mobile drawer
 * (`mobile-nav.tsx`) import this. They used to keep separate copies; the mobile
 * one never got the `/school/*` handling, so narrow widths resolved differently
 * from wide ones. `navigation-key.test.ts` asserts every configured item URL
 * round-trips back to its own key, which is what catches a new `/school/<x>`
 * section that forgets to register here (SCH-1943).
 */

/**
 * The `/school/*` sections, matched on the second path segment. Exported so the
 * test can anchor on this list instead of a second hardcoded copy: a new school
 * section added to `navigationConfig` but not registered here fails the test.
 */
export const SCHOOL_SECTION_KEYS = ['manage', 'comms', 'events', 'support'] as const

/**
 * The `navigationConfig` key for `pathname`, or `null` when there is no
 * pathname. A returned key is not guaranteed to exist in `navigationConfig` —
 * callers still check before indexing.
 */
export function getNavigationKey(pathname: string | null | undefined): string | null {
  if (!pathname) return null

  // Special handling for school sections: the first segment is always "school",
  // so key on the section that follows it.
  for (const section of SCHOOL_SECTION_KEYS) {
    if (pathname === `/school/${section}` || pathname.startsWith(`/school/${section}/`)) {
      return section
    }
  }

  // Default: use first path segment (/admin/*, /settings/*, ...).
  const pathSegment = pathname.split('/')[1]
  return pathSegment || null
}
